import { useEffect, useRef, useState } from 'react';

/**
 * C17 counter: counts from 0 to `target` over 1600ms (ease-out) once the
 * element scrolls into view. Reduced motion, no IntersectionObserver or SSR:
 * shows the final value immediately. The DOM always renders a number.
 *
 *   const { ref, value } = useCountUp(1997);
 *   <span ref={ref} className="ve-stat-number">{value}</span>
 */
export interface CountUpOptions {
  duration?: number;
  /** Decimal places to show while counting. */
  decimals?: number;
  /** Start value. */
  from?: number;
}

export interface CountUp<T extends HTMLElement = HTMLElement> {
  ref: React.RefObject<T>;
  value: string;
  done: boolean;
}

function format(n: number, decimals: number): string {
  return n.toLocaleString('en-US', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
}

export default function useCountUp<T extends HTMLElement = HTMLElement>(
  target: number,
  { duration = 1600, decimals = 0, from = 0 }: CountUpOptions = {},
): CountUp<T> {
  const ref = useRef<T>(null);
  // SSR + first client paint show the final value (no hidden content, no CLS).
  const [display, setDisplay] = useState(() => format(target, decimals));
  const [done, setDone] = useState(false);
  const started = useRef(false);

  useEffect(() => {
    const node = ref.current;
    if (!node || started.current) return;
    if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      setDone(true);
      return;
    }
    let raf = 0;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting || started.current) return;
        started.current = true;
        observer.disconnect();
        const start = performance.now();
        const tick = (now: number) => {
          const t = Math.min(1, (now - start) / duration);
          const eased = 1 - Math.pow(1 - t, 3);
          setDisplay(format(from + (target - from) * eased, decimals));
          if (t < 1) raf = requestAnimationFrame(tick);
          else setDone(true);
        };
        setDisplay(format(from, decimals));
        raf = requestAnimationFrame(tick);
      },
      { threshold: 0.4 },
    );
    observer.observe(node);
    return () => {
      observer.disconnect();
      cancelAnimationFrame(raf);
    };
  }, [target, duration, decimals, from]);

  return { ref, value: display, done };
}
